import React from "react"
import * as CheckboxPrimitive from "@radix-ui/react-checkbox"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Checkbox - Accessible checkbox component
 * 
 * Używa Radix UI Checkbox primitive
 * - Rozmiary: "1" | "2" | "3"
 * - Variants: "classic" | "soft" | "surface"
 * - Pełna obsługa klawiatury i screen readers
 * 
 * @example
 * ```tsx
 * <Checkbox 
 *   checked={accepted} 
 *   onCheckedChange={setAccepted}
 *   id="terms"
 * />
 * <Label htmlFor="terms">Akceptuję regulamin</Label>
 * ```
 */

const checkboxVariants = cva(
  cn(
    "peer shrink-0",
    "inline-flex items-center justify-center",
    "border transition-all duration-150",
    "focus-visible:outline-none focus-visible:ring-2",
    "focus-visible:ring-[var(--pink-9)] focus-visible:ring-offset-2",
    "focus-visible:ring-offset-[var(--dark-1)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "data-[state=checked]:bg-[var(--pink-9)]",
    "data-[state=checked]:border-[var(--pink-9)]",
    "data-[state=checked]:text-white"
  ),
  {
    variants: {
      variant: {
        classic: cn(
          "bg-[var(--dark-3)]",
          "border-[var(--dark-a7)]",
          "hover:border-[var(--pink-a6)]"
        ),
        soft: cn(
          "bg-[var(--gray-a3)]",
          "border-transparent",
          "hover:bg-[var(--gray-a4)]"
        ),
        surface: cn(
          "bg-[var(--dark-a2)]",
          "border-[var(--dark-a5)]",
          "hover:border-[var(--pink-a5)]"
        ),
      },
      size: {
        "1": cn(
          "h-[16px] w-[16px]",
          "rounded-[var(--radius-sm)]"
        ),
        "2": cn(
          "h-[20px] w-[20px]",
          "rounded-[var(--radius-base)]"
        ),
        "3": cn(
          "h-[24px] w-[24px]",
          "rounded-[var(--radius-md)]"
        ),
      },
    },
    defaultVariants: {
      variant: "classic",
      size: "2",
    },
  }
)

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant, size, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(checkboxVariants({ variant, size }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn("flex items-center justify-center text-current")}
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-3.5 w-3.5"
      >
        <path
          d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        />
      </svg>
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox, checkboxVariants }

